import React, { useContext, useMemo } from 'react';
import { Mask, TabBar } from 'antd-mobile';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { DollarSign, HelpCircle, Icon, Map, Plus } from 'react-feather';
import styles from './styles.module.pcss';
import { useReactiveVar } from '@apollo/client';
import { currentUserVar } from '@/graphql/operations/user';
import useDetectPWAMode from '@/utils/hooks/useDetectPWAMode';
import LayoutContext, { IFlexUserLayoutContext } from '@/context/FlexUserLayoutContext';
import { ResourceUIType } from '@/service/Api/types';
import Widgets from '@/flex-user-app/Widgets';
import useGetLayoutPermissions from '@/utils/hooks/useGetLayoutPermissions';
import getAryaConfig from '@/utils/getAryaConfig';

const BottomTabBar: React.FC = () => {
  const { isPWA } = useDetectPWAMode();
  const layoutContext = useContext<IFlexUserLayoutContext>(LayoutContext);
  const currentUser = useReactiveVar(currentUserVar);
  const location = useLocation();
  const { pathname } = location;
  const { layoutPermissions } = useGetLayoutPermissions(
    [
      ResourceUIType.Dashboard,
      ResourceUIType.Schedules,
      ResourceUIType.Timekeeping,
      ResourceUIType.Availability,
      ResourceUIType.Approvals,
      ResourceUIType.Compliance,
      ResourceUIType.Earnings,
    ],
    currentUser.permissions?.layoutAccess
  );

  const tabs = useMemo(() => {
    return [
      {
        path: '/dashboard',
        icon: Map,
        params: currentUser.token,
        disabled: !layoutPermissions.get(ResourceUIType.Dashboard)?.includes('read'),
      },
      {
        path: '/my-widgets',
        icon: Plus,
        className: styles.widget,
        disabled: ![
          ResourceUIType.Schedules,
          ResourceUIType.Timekeeping,
          ResourceUIType.Availability,
          ResourceUIType.Approvals,
          ResourceUIType.Compliance,
        ].some((resource) => layoutPermissions.get(resource)?.includes('read')),
      },
      {
        path: '/my-pay',
        icon: DollarSign,
        params: currentUser.token,
        disabled: !layoutPermissions.get(ResourceUIType.Earnings)?.includes('read'),
      },
      {
        path: '/help',
        icon: HelpCircle,
        url: getAryaConfig('ARYA_HELP_URL'),
        params: currentUser.token,
        disabled: false,
      },
    ];
  }, [currentUser.token, layoutPermissions]);

  const toggleWidgetsMask = () => {
    layoutContext.toggleWidgetMask?.();
  };

  const onChange = (key: string) => {
    if (key === '/my-widgets') {
      toggleWidgetsMask();
    }
  };

  const renderNavIcon = (tabPath: string, TabBarIcon: Icon, color: string) => {
    if (tabPath.startsWith('/my-widgets')) {
      return <TabBarIcon color={color} size={30} />;
    }

    const linkHrefProps = tabPath.startsWith('http') ? { href: tabPath, target: '_blank' } : {};
    return (
      <Link to={tabPath} replace={tabPath === location.pathname} {...linkHrefProps}>
        <TabBarIcon color={color} size={24} />
      </Link>
    );
  };

  return (
    <div className={styles.root}>
      <TabBar safeArea activeKey={pathname} onChange={onChange} className={isPWA ? styles.tabBar : ''}>
        {tabs
          .filter((tab) => {
            if (currentUser.isAuthenticated && !tab.disabled) {
              return true;
            }
            return !!tab.params;
          })
          .map((item) => (
            <TabBar.Item
              data-testid={item.path.replace(/^\//, '')}
              key={item.path}
              icon={(isActive) => {
                const tabPath = item.url ? item.url : item.params ? `${item.path}${item.params}` : `${item.path}`;
                if (isActive) {
                  return renderNavIcon(tabPath, item.icon, '#FFFFFF');
                }
                return renderNavIcon(tabPath, item.icon, '#202020');
              }}
              className={classNames(styles.item, {
                [styles.active]: item.path === pathname,
                [styles.widgetIcon]: item.path === '/my-widgets',
                [styles.widgetIconActive]: layoutContext.widgetsMaskVisible,
              })}
            />
          ))}
      </TabBar>
      <Mask
        visible={layoutContext.widgetsMaskVisible}
        opacity="thin"
        className={isPWA ? styles.widgetsMaskPwa : styles.widgetsMask}
        onMaskClick={toggleWidgetsMask}
        destroyOnClose
      >
        <Widgets />
      </Mask>
    </div>
  );
};

export default BottomTabBar;
